import { Link } from "react-router-dom";
import { ArrowRight, Wrench, Toolbox, Broom, Trash, Truck, CheckCircle, Phone, Tag, MagnifyingGlass } from "@phosphor-icons/react";
import { PILLARS, BUSINESS } from "@/data/site";

const PILLAR_ICON = { wrench: Wrench, toolbox: Toolbox, broom: Broom, trash: Trash, truck: Truck };

const PROCESS = [
  { step: "01", title: "Call or Request", desc: "Tell us the job — appliance issue, courier route, or home task. We listen first." },
  { step: "02", title: "Free Estimate", desc: "We quote the work up-front at no cost. No surprise fees, no pressure." },
  { step: "03", title: "Do the Work", desc: "Most appliance jobs same visit. Courier runs go out fast. Home jobs scheduled clean." },
  { step: "04", title: "Stand Behind It", desc: "We don't leave until it's right — and our work is guaranteed." },
];

export default function ServicesPage() {
  return (
    <div data-testid="services-page">
      {/* Hero */}
      <section className="ji-container pt-16 md:pt-24 pb-12">
        <div className="ji-overline mb-5 inline-flex items-center gap-2">
          <Tag size={12} weight="fill" /> Free estimates &amp; quotes on every job
        </div>
        <h1 style={{ fontFamily: "Cabinet Grotesk", lineHeight: 1.0 }} className="text-5xl md:text-6xl font-bold tracking-tight max-w-3xl">
          Everything under<br /><span className="text-[var(--ji-accent)] italic">one roof.</span>
        </h1>
        <p className="mt-6 max-w-2xl text-lg text-[var(--ji-muted)] leading-relaxed">
          From appliance repair to heavy lifting — we run every service line with a single crew, so you get one number to call, one quote to read, and one team that owns the outcome.
        </p>
      </section>

      {/* PILLARS — alternating layout */}
      {PILLARS.map((p, idx) => {
        const Icon = PILLAR_ICON[p.iconKey] || Wrench;
        const flipped = idx % 2 === 1;
        const sectionBg = idx % 2 === 1 ? "bg-[var(--ji-surface-alt)] border-y border-[var(--ji-border)]" : "";
        return (
          <section key={p.key} className={sectionBg} data-testid={`pillar-section-${p.key}`}>
            <div className="ji-container py-16 md:py-24">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
                <div className={`lg:col-span-5 ${flipped ? "lg:order-2" : ""}`}>
                  <div className="ji-card p-8" style={{ borderRadius: 4 }}>
                    <span className="inline-flex w-14 h-14 items-center justify-center text-[var(--ji-primary)] bg-[var(--ji-surface-alt)]" style={{ borderRadius: 4 }}>
                      <Icon size={28} weight="duotone" />
                    </span>
                    <div className="mt-5 text-xs uppercase tracking-[0.22em] text-[var(--ji-accent)]">{p.overline}</div>
                    <h2 style={{ fontFamily: "Cabinet Grotesk", lineHeight: 1.05 }} className="mt-2 text-3xl md:text-4xl font-bold tracking-tight">
                      {p.title}
                    </h2>
                    <p className="mt-3 text-sm font-medium text-[var(--ji-text)]">{p.short}</p>
                    <p className="mt-4 text-[var(--ji-muted)] leading-relaxed">{p.desc}</p>

                    <div className="mt-8 flex flex-wrap gap-3">
                      <Link to="/contact" data-testid={`pillar-cta-quote-${p.key}`} className="ji-btn-primary inline-flex items-center gap-2 px-5 h-12 text-sm font-semibold" style={{ borderRadius: 4 }}>
                        <Tag size={14} weight="bold" /> Get Free Quote
                      </Link>
                      {p.key === "appliance-repair" && (
                        <Link to="/diagnostic" data-testid="pillar-cta-diagnostic-appliance-repair" className="ji-btn-accent inline-flex items-center gap-2 px-5 h-12 text-sm font-semibold" style={{ borderRadius: 4 }}>
                          <MagnifyingGlass size={14} weight="bold" /> Book $79 Diagnostic
                        </Link>
                      )}
                      {p.key !== "appliance-repair" && (
                        <Link to={`/deposit?tier=standard&for=${encodeURIComponent(p.title)}`} data-testid={`pillar-cta-book-${p.key}`} className="ji-btn-accent inline-flex items-center gap-2 px-5 h-12 text-sm font-semibold" style={{ borderRadius: 4 }}>
                          <Tag size={14} weight="bold" /> Book $79 · Credited to final
                        </Link>
                      )}
                      <a href={`tel:${BUSINESS.primaryPhone.tel}`} data-testid={`pillar-cta-call-${p.key}`} className="ji-btn-outline inline-flex items-center gap-2 px-5 h-12 text-sm font-semibold" style={{ borderRadius: 4 }}>
                        <Phone size={14} weight="bold" /> Call now
                      </a>
                    </div>
                  </div>
                </div>

                <div className={`lg:col-span-7 ${flipped ? "lg:order-1" : ""}`}>
                  {p.image && (
                    <div className="mb-6 overflow-hidden ji-card" style={{ borderRadius: 4 }} data-testid={`pillar-image-${p.key}`}>
                      <img
                        src={p.image}
                        alt={p.title}
                        loading="lazy"
                        className="w-full h-[240px] md:h-[300px] object-cover"
                      />
                    </div>
                  )}
                  <div className="ji-overline mb-4">What's included</div>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {p.items.map((it, i) => (
                      <li
                        key={i}
                        data-testid={`pillar-item-${p.key}-${i}`}
                        className="ji-card flex items-start gap-3 p-5"
                        style={{ borderRadius: 4 }}
                      >
                        <CheckCircle size={18} weight="duotone" className="text-[var(--ji-accent)] mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-[var(--ji-text)] leading-relaxed">{it}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </section>
        );
      })}

      {/* Process */}
      <section className="bg-[var(--ji-surface-alt)] border-y border-[var(--ji-border)]" data-testid="process-section">
        <div className="ji-container py-20 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <div className="ji-overline mb-4 inline-flex items-center gap-2">
                <Tag size={12} weight="fill" /> Free estimate · zero pressure
              </div>
              <h2 style={{ fontFamily: "Cabinet Grotesk" }} className="text-4xl md:text-5xl font-bold tracking-tight">
                Four steps,<br />zero surprises.
              </h2>
            </div>
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
              {PROCESS.map((p) => (
                <div key={p.step} className="ji-card p-6" style={{ borderRadius: 4 }} data-testid={`process-${p.step}`}>
                  <div style={{ fontFamily: "Cabinet Grotesk" }} className="text-3xl font-bold tracking-tight text-[var(--ji-accent)]">{p.step}</div>
                  <h3 style={{ fontFamily: "Cabinet Grotesk" }} className="mt-3 text-xl font-bold tracking-tight">{p.title}</h3>
                  <p className="mt-2 text-sm text-[var(--ji-muted)] leading-relaxed">{p.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="ji-container py-20 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Link to="/contact" data-testid="services-cta-request" className="ji-btn-primary inline-flex items-center justify-between p-8 group" style={{ borderRadius: 4 }}>
            <div>
              <div className="text-xs uppercase tracking-[0.22em] opacity-80">Free · No obligation</div>
              <div style={{ fontFamily: "Cabinet Grotesk" }} className="text-2xl md:text-3xl font-bold tracking-tight mt-1">Request a Free Quote →</div>
            </div>
            <ArrowRight size={28} weight="bold" />
          </Link>
          <a href={`tel:${BUSINESS.primaryPhone.tel}`} data-testid="services-cta-call" className="ji-btn-accent inline-flex items-center justify-between p-8 group" style={{ borderRadius: 4 }}>
            <div>
              <div className="text-xs uppercase tracking-[0.22em] opacity-90">Talk to David</div>
              <div style={{ fontFamily: "Cabinet Grotesk" }} className="text-2xl md:text-3xl font-bold tracking-tight mt-1">{BUSINESS.primaryPhone.display}</div>
            </div>
            <Phone size={28} weight="bold" />
          </a>
        </div>
      </section>
    </div>
  );
}
